import React, { useContext, useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { ChevronRight, Check, X, Lock } from 'lucide-react'
import { AuthContext } from '@/context/AuthContext'
import { DashboardContext } from '@/context/DashboardContext'
import { CreateProductDialog } from '@/mobile/dashboard/dialog/CreateProductDialog'
import { ImportFiles } from '@/mobile/dashboard/dialog/importFiles'
import { IntegrationsModal } from '@/mobile/dashboard/integrations/IntegrationsModal'
import { OnboardingAddMember } from '@/mobile/dashboard/OnboardingAddMember'
import { useCustomFetch } from '@/hooks/useCustomFetch'

interface ChecklistItem {
  id: string
  title: string
  description: string
  emoji: string
  buttonText: string
  skipText?: string
  completed: boolean
  skipped: boolean
  active: boolean
}

export function OnboardingWelcomePage() {
  const { user } = useContext(AuthContext)
  const { products, setAllProducts, activeWorkspace, setMeetings, allProjects } = useContext(DashboardContext)
  const customFetch = useCustomFetch()

  // Track manually skipped items (persisted per workspace)
  const [skippedItems, setSkippedItems] = useState<Set<string>>(
    () => new Set(activeWorkspace?.onboarding_skipped_steps || [])
  )

  // Track pending invitations
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([])
  const [invitationsLoading, setInvitationsLoading] = useState(true)
  // Track existing workspace members (accepted)
  const [workspaceMembers, setWorkspaceMembers] = useState<any[]>([])
  const [membersLoading, setMembersLoading] = useState(true)

  // Track which step is currently being worked on
  const [currentStep, setCurrentStep] = useState<string | null>(null)

  // Track completed steps (persisted per workspace)
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(() => {
    const saved = activeWorkspace?.onboarding_completed_steps || []
    return new Set(saved)
  })

  // Track loading states for different actions
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({})

  // Function to set loading state for a specific action
  const setActionLoading = (actionId: string, loading: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [actionId]: loading }))
  }

  // Function to check if an action is loading
  const isActionLoading = (actionId: string) => loadingStates[actionId] || false

  // Function to fetch pending invitations
  const fetchInvitations = useCallback(async () => {
    if (!user?.active_workspace) return

    try {
      const response = await customFetch(`/getInvitesByWorkspace/${user.active_workspace}`, 'GET')
      setPendingInvitations(response.users || [])
    } catch {
      setPendingInvitations([])
    } finally {
      setInvitationsLoading(false)
    }
  }, [user?.active_workspace, customFetch])

  // Function to fetch accepted workspace members
  const fetchMembers = useCallback(async () => {
    if (!user?.active_workspace) return
    try {
      const response = await customFetch(`/getUsersByWorkspace/${user.active_workspace}`, 'GET')
      setWorkspaceMembers(response.users || [])
    } catch {
      setWorkspaceMembers([])
    } finally {
      setMembersLoading(false)
    }
  }, [user?.active_workspace, customFetch])

  // Custom handler for successful team invites from onboarding
  const handleTeamInviteSuccess = async () => {
    // Mark team step as completed
    await handleStepComplete('team')
    setShowInviteTeamDialog(false)
  }

  // Refs for the dynamic vertical line
  const lineRef = useRef<HTMLDivElement>(null)
  const lastCircleRef = useRef<HTMLDivElement>(null)

  // Dialog state
  const [createProductDialogOpen, setCreateProductDialogOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false)
  const [showInviteTeamDialog, setShowInviteTeamDialog] = useState(false)

  // Reset skipped items when workspace changes
  useEffect(() => {
    if (activeWorkspace?.id) {
      setSkippedItems(new Set(activeWorkspace?.onboarding_skipped_steps || []))
    }
  }, [activeWorkspace?.id, activeWorkspace?.onboarding_skipped_steps])

  // Reset completed steps when workspace changes
  useEffect(() => {
    if (activeWorkspace?.id) {
      const saved = activeWorkspace?.onboarding_completed_steps || []
      setCompletedSteps(new Set(saved as string[]))
    }
  }, [activeWorkspace?.id, activeWorkspace?.onboarding_completed_steps])

  // Reset loading states when workspace changes
  useEffect(() => {
    if (user?.active_workspace) {
      setInvitationsLoading(true)
      setMembersLoading(true)
    }
  }, [user?.active_workspace])

  // Fetch invitations when user/workspace changes
  useEffect(() => {
    if (user?.active_workspace) {
      fetchInvitations()
    }
  }, [user?.active_workspace, fetchInvitations])

  // Fetch members when user/workspace changes
  useEffect(() => {
    if (user?.active_workspace) {
      fetchMembers()
    }
  }, [user?.active_workspace, fetchMembers])

  // Monitor OAuth success for integrations
  useEffect(() => {
    const oauthSuccess = localStorage.getItem('oauth_success')
    if (oauthSuccess === 'true') {
      // Clear the flag
      localStorage.removeItem('oauth_success')
      localStorage.removeItem('oauth_success_type')
      // Mark integrations step as completed
      handleStepComplete('integrations')
    }
  }, [])

  // Helper function to check if a step is completed
  const checkStepCompletion = (stepId: string): boolean => {
    return completedSteps.has(stepId)
  }

  // Function to mark a step as completed
  const markStepCompleted = async (stepId: string) => {
    setActionLoading(stepId, true)

    try {
      const newCompletedSteps = new Set(completedSteps)
      newCompletedSteps.add(stepId)
      setCompletedSteps(newCompletedSteps)

      // Update workspace with completed steps
      if (activeWorkspace?.id) {
        const completedStepsArray = Array.from(newCompletedSteps)
        await customFetch('/updateWorkspace', 'PUT', {
          workspaceId: activeWorkspace.id,
          updates: { onboarding_completed_steps: completedStepsArray },
        })
      }
    } catch (error) {
      console.error('Error in markStepCompleted:', error)
    } finally {
      setActionLoading(stepId, false)
    }
  }

  // Function to handle step completion
  const handleStepComplete = async (stepId: string) => {
    await markStepCompleted(stepId)
    setCurrentStep(null) // Close any open dialogs
  }

  // Calculate stepper state
  const checklistItems: ChecklistItem[] = useMemo(() => {
    const items = [
      {
        id: 'product',
        title: 'Describe Your Product',
        description: 'Add your product name, tagline, and description',
        emoji: '📦',
        buttonText: 'Create Product',
        skipText: 'Skip for now',
      },
      {
        id: 'recordings',
        title: 'Upload Call Recordings',
        description: 'Upload at least one user interview, sales meeting, or support call',
        emoji: '🎥',
        buttonText: 'Upload Recordings',
        skipText: 'Skip for now',
      },
      {
        id: 'integrations',
        title: 'Connect Integrations',
        description: 'Link your Zoom, Calendar, and other accounts.',
        emoji: '🔗',
        buttonText: 'Select Integrations',
        skipText: 'Skip for now',
      },
      {
        id: 'team',
        title: 'Invite Team',
        description: 'Add team members to collaborate on insights',
        emoji: '👥',
        buttonText: 'Invite Team',
        skipText: 'Skip for now',
      },
    ]

    // Calculate completion and active states
    const result = items.map((item, index) => {
      const completed = checkStepCompletion(item.id)
      const skipped = skippedItems.has(item.id)

      // First step is always active
      if (index === 0) {
        return {
          ...item,
          completed,
          skipped,
          active: true,
        }
      }

      // For subsequent steps, check if the previous step is completed
      const previousStep = items[index - 1]
      const previousCompleted = checkStepCompletion(previousStep.id)
      const previousSkipped = skippedItems.has(previousStep.id)

      const active = previousCompleted || previousSkipped

      return {
        ...item,
        completed,
        skipped,
        active,
      }
    })

    return result
  }, [completedSteps, skippedItems])

  // Handle skip action
  const handleSkip = async (stepId: string) => {
    setActionLoading(stepId, true)

    try {
      const newSkippedItems = new Set(skippedItems)
      newSkippedItems.add(stepId)
      setSkippedItems(newSkippedItems)

      // Also mark as completed when skipped
      await markStepCompleted(stepId)

      // Update workspace with skipped steps
      if (activeWorkspace?.id) {
        const skippedStepsArray = Array.from(newSkippedItems)
        await customFetch('/updateWorkspace', 'PUT', {
          workspaceId: activeWorkspace.id,
          updates: { onboarding_skipped_steps: skippedStepsArray },
        })
        // Don't refresh the active workspace here as it will override our local state
        // The Dashboard component will get updated when it next fetches workspace data
      }
    } catch {
      // Handle error if needed
    } finally {
      setActionLoading(stepId, false)
    }
  }

  // Calculate progress
  const completedCount = checklistItems.filter((item) => item.completed || item.skipped).length
  const progressPercentage = (completedCount / checklistItems.length) * 100

  // Update vertical line height after rendering and on resize
  const updateLineHeight = useCallback(() => {
    if (!lineRef.current || !lastCircleRef.current) return
    const containerRect = lineRef.current.parentElement?.getBoundingClientRect()
    const lastCircleRect = lastCircleRef.current.getBoundingClientRect()
    if (containerRect && lastCircleRect) {
      const height = lastCircleRect.top + lastCircleRect.height / 2 - containerRect.top
      lineRef.current.style.height = `${height}px`
    }
  }, [])

  useEffect(() => {
    updateLineHeight()
    window.addEventListener('resize', updateLineHeight)
    return () => window.removeEventListener('resize', updateLineHeight)
  }, [updateLineHeight, checklistItems.length])

  // Get the user's first name and capitalize it
  const userName = (() => {
    const name = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'there'
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
  })()

  // Helper function to find the Discovery project for the current product
  const getDiscoveryProject = () => {
    if (!allProjects || allProjects.length === 0) {
      return null
    }
    // Find the Discovery project within this product's projects
    const discoveryProject = allProjects.find((project) => project.name === 'Discovery')

    return discoveryProject || null
  }

  return (
    <div className="h-full overflow-y-auto bg-canvas">
      <div className="max-w-4xl mx-auto pb-0 pl-4 pr-4 pt-16">
        {/* Header */}
        <div className="mb-8">
          {/* Title and Subtitle - Stacked */}
          <div className="text-left mb-6">
            <h1 className="text-2xl font-bold text-zinc mb-2">Welcome, {userName}! 👋</h1>
            <p className="text-sm text-slate">Complete these steps to see roadmap suggestions.</p>
          </div>

          {/* Progress Bar - Full Width Below Title */}
          <div className="w-full">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-slate">Progress</span>
              <span className="text-xs font-medium text-zinc">
                {completedCount}/{checklistItems.length} completed
              </span>
            </div>
            <Progress value={progressPercentage} className="h-2 bg-white [&>div]:bg-pink" />
          </div>
        </div>

        {/* Quick Start Section */}
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-zinc mb-6 text-left">Quick start</h4>

          {/* Timeline/Stepper */}
          <div className="relative">
            {/* Vertical line */}
            <div ref={lineRef} className="absolute left-5 top-0 w-0.5 bg-gray-200"></div>

            <div className="space-y-6">
              {checklistItems.map((item, index) => {
                // Define visual states based on item status
                const getStepperClasses = () => {
                  if (item.completed) {
                    return 'w-10 h-10 bg-green-500 border-2 border-green-500 rounded-full flex items-center justify-center shadow-sm'
                  } else if (item.skipped) {
                    return 'w-10 h-10 bg-gray-400 border-2 border-gray-400 rounded-full flex items-center justify-center shadow-sm'
                  } else if (item.active) {
                    return 'w-10 h-10 bg-white border-2 border-pink rounded-full flex items-center justify-center shadow-sm'
                  } else {
                    return 'w-10 h-10 bg-gray-100 border-2 border-gray-300 rounded-full flex items-center justify-center shadow-sm'
                  }
                }

                const getStepperIcon = () => {
                  if (item.completed) {
                    return <Check className="w-5 h-5 text-white" />
                  } else if (item.skipped) {
                    return <X className="w-5 h-5 text-white" />
                  } else {
                    return (
                      <span className={`text-sm font-bold ${item.active ? 'text-pink' : 'text-gray-400'}`}>
                        {index + 1}
                      </span>
                    )
                  }
                }

                const getContentClasses = () => {
                  const baseClasses = 'flex-1 min-w-0 rounded-lg shadow-sm border p-4'
                  if (item.completed) {
                    return `${baseClasses} bg-white border-green-500`
                  } else if (item.skipped) {
                    return `${baseClasses} bg-gray-50 border-gray-200`
                  } else if (item.active) {
                    return `${baseClasses} bg-white border-gray-200`
                  } else {
                    return `${baseClasses} bg-gray-50 border-gray-300 opacity-50`
                  }
                }

                return (
                  <div key={item.id} className="relative flex items-start space-x-3">
                    {/* Step circle with dynamic state */}
                    <div
                      className="relative z-10 flex-shrink-0"
                      ref={index === checklistItems.length - 1 ? lastCircleRef : undefined}
                    >
                      <div className={getStepperClasses()}>{getStepperIcon()}</div>
                    </div>

                    {/* Content */}
                    <div className={getContentClasses()}>
                      {/* Stack content vertically on mobile */}
                      <div className="flex flex-col space-y-4">
                        {/* Top section: Emoji, Title and Description */}
                        <div className="flex items-start gap-3">
                          {/* Emoji in rounded grey square */}
                          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <span className="text-xl">{item.emoji}</span>
                          </div>

                          {/* Title and description */}
                          <div className="flex-1 min-w-0">
                            <h3
                              className={`text-base font-semibold mb-1 flex flex-wrap items-center gap-2 ${
                                item.active || item.completed || item.skipped ? 'text-zinc' : 'text-gray-400'
                              }`}
                            >
                              <span>{item.title}</span>
                              {item.skipped && (
                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                                  Skipped
                                </span>
                              )}
                            </h3>
                            <p
                              className={`text-xs leading-relaxed ${
                                item.active || item.completed || item.skipped ? 'text-slate' : 'text-gray-400'
                              }`}
                            >
                              {item.description}
                            </p>
                          </div>
                        </div>

                        {/* Action Buttons - Full width below content */}
                        <div className="flex flex-col gap-2 w-full">
                          {/* Show buttons for active steps that are not completed or skipped */}
                          {item.active && !item.completed && !item.skipped && (
                            <>
                              <Button
                                className="bg-pink hover:bg-pink/90 text-white w-full h-11 text-sm"
                                onClick={() => {
                                  setActionLoading(item.id, true)
                                  try {
                                    if (item.id === 'product') {
                                      setCurrentStep('product')
                                      setCreateProductDialogOpen(true)
                                    } else if (item.id === 'recordings') {
                                      setCurrentStep('recordings')
                                      setShowImportDialog(true)
                                    } else if (item.id === 'integrations') {
                                      setCurrentStep('integrations')
                                      setShowIntegrationsModal(true)
                                    } else if (item.id === 'team') {
                                      setCurrentStep('team')
                                      setShowInviteTeamDialog(true)
                                    } else {
                                      // TODO: Add navigation logic for other actions
                                    }
                                  } finally {
                                    setActionLoading(item.id, false)
                                  }
                                }}
                                disabled={isActionLoading(item.id)}
                              >
                                {item.buttonText}
                                <ChevronRight className="w-4 h-4 ml-1" />
                                {isActionLoading(item.id) && <span className="ml-2">...</span>}
                              </Button>
                              {/* Only show skip button for steps other than 'product' */}
                              {item.id !== 'product' && (
                                <Button
                                  variant="outline"
                                  className="text-gray-600 border-gray-300 hover:bg-gray-50 w-full h-11 text-sm"
                                  onClick={() => handleSkip(item.id)}
                                  disabled={isActionLoading(item.id)}
                                >
                                  {item.skipText || 'Skip'}
                                  {isActionLoading(item.id) && <span className="ml-2">...</span>}
                                </Button>
                              )}
                            </>
                          )}
                          {/* Show buttons for completed or skipped steps */}
                          {(item.completed || item.skipped) && (
                            <Button
                              variant="outline"
                              className="text-gray-600 border-gray-300 hover:bg-gray-50 w-full h-11 text-sm"
                              onClick={() => {
                                setActionLoading(item.id, true)
                                try {
                                  if (item.id === 'product' && products && products.length > 0) {
                                    setEditingProduct(products[0]) // Pass the first product for editing
                                    setCreateProductDialogOpen(true)
                                  } else if (item.id === 'recordings') {
                                    const discoveryProject = getDiscoveryProject()
                                    if (discoveryProject) {
                                      setShowImportDialog(true)
                                    } else {
                                      // No Discovery project found for upload
                                    }
                                  } else if (item.id === 'integrations') {
                                    setShowIntegrationsModal(true)
                                  } else if (item.id === 'team') {
                                    setShowInviteTeamDialog(true)
                                  } else {
                                    // TODO: Add logic to modify/redo other actions
                                  }
                                } finally {
                                  setActionLoading(item.id, false)
                                }
                              }}
                              disabled={isActionLoading(item.id)}
                            >
                              {item.id === 'recordings' ? 'Add Recordings' : 'Update'}
                              {isActionLoading(item.id) && <span className="ml-2">...</span>}
                            </Button>
                          )}
                          {!item.active && !item.completed && !item.skipped && (
                            <div className="text-gray-400 w-full h-11 flex items-center justify-center">
                              <Lock className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Create Product Dialog */}
      <CreateProductDialog
        open={createProductDialogOpen}
        onOpenChange={(open) => {
          setCreateProductDialogOpen(open)
          if (!open) {
            setEditingProduct(null) // Clear editing product when dialog closes
            setCurrentStep(null) // Reset current step when dialog closes
          }
        }}
        onSuccess={async () => {
          // Mark complete FIRST, before any data refresh
          await handleStepComplete('product')

          // Then refresh products (without refreshing workspace again to avoid race condition)
          await setAllProducts()

          setCreateProductDialogOpen(false)
        }}
        simplified={true}
        title={editingProduct ? 'Edit Product' : 'Create Product'}
        fitContent={true}
        product={editingProduct}
      />

      {/* Import Files Dialog for Upload Recording */}
      {showImportDialog && getDiscoveryProject() && (
        <ImportFiles
          projectId={getDiscoveryProject()!.id}
          projectName={getDiscoveryProject()!.name}
          setMeetings={async (updatedMeetings) => {
            setMeetings(updatedMeetings)
            // Mark recordings step as completed
            await handleStepComplete('recordings')
          }}
          handleClose={() => {
            setShowImportDialog(false)
            setCurrentStep(null) // Reset current step when dialog closes
          }}
          customNote="Upload your call recordings as Audio or Video files to be processed. Upload up to 10 files, each up to 2GB. Acceptable file types: MP3, MP4, MP2, WAV, FLAC, PCM, M4A, Ogg, Opus, WebM."
          skipNavigation={true}
        />
      )}

      {/* Integrations Modal */}
      <IntegrationsModal
        open={showIntegrationsModal}
        onClose={() => {
          setShowIntegrationsModal(false)
          setCurrentStep(null) // Reset current step when dialog closes
        }}
      />

      {/* Invite Team Dialog */}
      <OnboardingAddMember
        open={showInviteTeamDialog}
        onOpenChange={(open) => {
          setShowInviteTeamDialog(open)
          if (!open) {
            setCurrentStep(null) // Reset current step when dialog closes
          }
        }}
        onSuccess={handleTeamInviteSuccess}
      />

      {/* Let's Get Started Button - Show when team step is completed or skipped */}
      {(checkStepCompletion('team') || skippedItems.has('team')) && (
        <div className="max-w-4xl mx-auto px-4 pb-8 pt-6">
          <Button
            className="bg-pink hover:bg-pink/90 text-white w-full h-12 text-base font-semibold shadow-lg"
            onClick={() => window.location.reload()}
          >
            Let's Get Started
          </Button>
        </div>
      )}
    </div>
  )
}
